// 웹(apps/web) ↔ 앱 껍데기(apps/mobile) 메시지 계약 — 양쪽이 이 한 벌만 쓴다.

export const BRIDGE_VERSION = 1 as const;

/** 앱→웹 메시지가 window 에 실려 오는 이벤트 이름. */
export const NATIVE_EVENT = "buildpy-native";

type Envelope<T extends string, P> = { v: typeof BRIDGE_VERSION; id: string; type: T; payload: P };
// 웹→앱 봉투엔 hello 로 받은 실행 열쇠(key)를 붙인다 — 앱은 열쇠가 맞는 것만 받는다(메인 프레임만 hello 를 받으므로 iframe 은 모른다).
type WebEnvelope<T extends string, P> = Envelope<T, P> & { key: string };

export type WebToNative =
  | WebEnvelope<"ready", { path: string }>
  | WebEnvelope<"openExternal", { url: string }>
  | WebEnvelope<"share", { title: string; url: string }>
  | WebEnvelope<"ping", { at: number }>
  | WebEnvelope<"backResult", { handled: boolean }>;

export type NativeToWeb =
  | Envelope<"hello", { platform: "ios" | "android"; appVersion: string; bridge: typeof BRIDGE_VERSION; key: string }>
  | Envelope<"insets", { top: number; bottom: number; left: number; right: number }>
  | Envelope<"back", Record<string, never>>
  | Envelope<"pong", { at: number }>;

type Rec = Record<string, unknown>;
type PayloadCheck = (p: Rec) => boolean;

const isRec = (x: unknown): x is Rec => typeof x === "object" && x !== null && !Array.isArray(x);
const isStr = (x: unknown): x is string => typeof x === "string";
const isNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

const WEB_TO_NATIVE: Record<WebToNative["type"], PayloadCheck> = {
  ready: (p) => isStr(p.path),
  openExternal: (p) => isStr(p.url),
  share: (p) => isStr(p.title) && isStr(p.url),
  ping: (p) => isNum(p.at),
  backResult: (p) => typeof p.handled === "boolean",
};

const NATIVE_TO_WEB: Record<NativeToWeb["type"], PayloadCheck> = {
  hello: (p) => (p.platform === "ios" || p.platform === "android") && isStr(p.appVersion) && p.bridge === BRIDGE_VERSION && isStr(p.key) && p.key !== "",
  insets: (p) => isNum(p.top) && isNum(p.bottom) && isNum(p.left) && isNum(p.right),
  back: () => true,
  pong: (p) => isNum(p.at),
};

export function encode(msg: WebToNative | NativeToWeb): string {
  return JSON.stringify(msg);
}

function parseWith<M>(checks: Record<string, PayloadCheck>, raw: unknown, keyed = false): M | null {
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!isRec(value) || value.v !== BRIDGE_VERSION || !isStr(value.id) || value.id === "" || !isStr(value.type)) return null;
  if (!Object.prototype.hasOwnProperty.call(checks, value.type) || !isRec(value.payload)) return null;
  if (keyed && (!isStr(value.key) || value.key === "")) return null;
  return checks[value.type](value.payload) ? (value as M) : null;
}

/** 웹뷰 onMessage 의 문자열(또는 이미 파싱된 값) → 메시지 · 모르는 type·v·깨진 JSON·열쇠 없음은 null(열쇠 값 대조는 앱이). */
export function parseWebToNative(raw: unknown): WebToNative | null {
  return parseWith<WebToNative>(WEB_TO_NATIVE, raw, true);
}

/** NATIVE_EVENT 의 event.data → 메시지 · 모르는 type·v·깨진 JSON 은 null. */
export function parseNativeToWeb(raw: unknown): NativeToWeb | null {
  return parseWith<NativeToWeb>(NATIVE_TO_WEB, raw);
}

// JS 문자열 리터럴 안에서 HTML 파서·옛 JS 엔진이 끊을 수 있는 글자를 \u 로 바꾼다.
const UNSAFE = /[<>&\u2028\u2029]/g;
const escapeChar = (c: string) => `\\u${c.charCodeAt(0).toString(16).padStart(4, "0")}`;

/** 웹뷰 injectJavaScript 에 넘길 문자열 — event.data 는 encode 한 JSON 문자열이다. */
export function injectScript(msg: NativeToWeb): string {
  const literal = JSON.stringify(encode(msg)).replace(UNSAFE, escapeChar);
  return `window.dispatchEvent(new MessageEvent(${JSON.stringify(NATIVE_EVENT)},{data:${literal}}));true;`;
}
